import { useMemo } from 'react'

import { QueryClient } from '@tanstack/react-query'
import { useSelector } from 'react-redux'

import {
    getCampaignFacts,
    getCampaignFactsExport,
} from 'api/Banner/campaignFactsApi'
import { CampaignFactRecord } from 'configuration/tables/campaignFactsTables'
import { formatFilters } from 'helpers/filters/campaignFacts'
import { formatAdAccountName } from 'helpers/formatting'
import { CobaltPath } from 'routing/Path'
import {
    selectResourceOfPage,
    selectResourceParamsOfPage,
    selectTableSettings,
    selectVisibleColumnsOfTable,
    selectVisibleMetricsOfTable,
} from 'selectors/ui'
import { Field, RootReduxState } from 'types'

import {
    CerebroDownloadQueryResult,
    CerebroPaginatedMetricDataQueryResult,
    useCerebroDownload,
    useCerebroPaginatedMetricDataQueryParams,
    useCerebroPaginatedMetricDataQuery,
    useCerebroDownloadParams,
    CerebroPaginatedMetricQueryWrapperProps,
    CerebroDownloadQueryWrapperProps,
} from '../CerebroPaginatedQuery'

const CAMPAIGN_QUERY_PREFIX = ['campaigns']

export async function invalidateCampaigns(
    queryClient: QueryClient
): Promise<void> {
    await queryClient.invalidateQueries({
        queryKey: CAMPAIGN_QUERY_PREFIX,
        type: 'all',
        refetchType: 'active',
    })
}

type CampaignSummaryTableDataQueryProps =
    CerebroPaginatedMetricQueryWrapperProps<CampaignFactRecord> & {
        config: Field<CampaignFactRecord>[]
        pagePath: CobaltPath
    }

export function useCampaignSummaryTableDataQuery({
    pagePath,
    tablePath,
    config,
    filters,
    currencyCode,
    options,
}: CampaignSummaryTableDataQueryProps): CerebroPaginatedMetricDataQueryResult<CampaignFactRecord> {
    const { columns } = useSelector((state: RootReduxState) =>
        selectTableSettings(state, tablePath)
    )
    const { adAccountId, portfolioId, labelId } = useSelector(
        (state: RootReduxState) =>
            selectResourceParamsOfPage(state, pagePath[0])
    )
    const additionalParams = useMemo(() => {
        const params: Record<string, unknown> = {}
        if (adAccountId) {
            params.profile = adAccountId
        }
        if (portfolioId) {
            params.portfolio__id = portfolioId
        }
        if (labelId) {
            params.campaign__labels_id_and_in = labelId
        }

        return params
    }, [adAccountId, labelId, portfolioId])

    // // Stream fields aren't automatically returned by Banner
    // // If the Budget Status column is visible, we manually query for them
    let group_by_stream_fields = ''
    if (
        columns.find(
            (column) =>
                column.isVisible && column.id === 'campaign__budget_status'
        )
    ) {
        group_by_stream_fields =
            ',budget_percentage,budget_updated_at,historical_budget'
    }

    const metrics = useSelector((state: RootReduxState) =>
        selectVisibleMetricsOfTable(state, tablePath, config)
    )

    const params = useCerebroPaginatedMetricDataQueryParams({
        tablePath,
        filters,
        currencyCode,
        groupBy: `campaign_id,profile_id,portfolio_id${group_by_stream_fields}`,
        formatFilters,
        metrics,
        additionalParams,
    })

    return useCerebroPaginatedMetricDataQuery<CampaignFactRecord>({
        queryKeyPrefix: CAMPAIGN_QUERY_PREFIX,
        params,
        options,
        apiCall: getCampaignFacts,
    })
}

type CampaignFactsDownloadProps<T> = CerebroDownloadQueryWrapperProps<T> & {
    pagePath: CobaltPath
    config: Field<T>[]
}

export function useCampaignFactsDownload({
    pagePath,
    tablePath,
    config,
    filters,
    currencyCode,
}: CampaignFactsDownloadProps<CampaignFactRecord>): CerebroDownloadQueryResult {
    const { adAccountId, portfolioId, labelId } = useSelector(
        (state: RootReduxState) =>
            selectResourceParamsOfPage(state, pagePath[0])
    )
    const resource = useSelector((state: RootReduxState) =>
        selectResourceOfPage(state, pagePath[0])
    )

    let reportName = 'All Campaigns Report'
    const additionalParams: Record<string, unknown> = {}
    if (adAccountId) {
        additionalParams.profile = adAccountId
        reportName = `Campaign Report - ${formatAdAccountName(resource)}`
    }
    if (portfolioId) {
        additionalParams.portfolio__id = portfolioId
        reportName = `Campaign Report - ${resource.name}`
    }
    if (labelId) {
        additionalParams.campaign__labels_id_and_in = labelId
        reportName = `Campaign Report - ${resource.name}`
    }

    const columns = useSelector((state: RootReduxState) =>
        selectVisibleColumnsOfTable(state, tablePath, config)
    )

    const params = useCerebroDownloadParams({
        tablePath,
        filters,
        columns,
        currencyCode,
        additionalParams,
        groupBy: 'campaign_id',
        formatFilters,
        reportName,
    })

    return useCerebroDownload({
        queryKeyPrefix: CAMPAIGN_QUERY_PREFIX,
        params,
        reportName,
        apiCall: getCampaignFactsExport,
    })
}
